import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Droppable } from 'react-beautiful-dnd';
import PropTypes from 'prop-types';
import classNames from 'classNames';
import LessonWorkspace from '~/components/courses/lesson-workspace';
import NewEntity from '~/components/common/new-entity';
import EmptyEntity from '~/components/common/empty-entity';

/**
 * Our canvas when building a course. Allows you to add lessons, topics and quizzes by drag and drop.
 * Lessons are handled by Beautiful React DnD, whereas the tree within a lesson is handled by React Sortable Tree
 * Separating the lessons from the tree provides us more flexibility for styling and selecting which lesson is active.
 */
class CourseWorkspace extends Component {
	/**
	 * Constructor
	 *
	 * @param {Objeect} props
	 */
	constructor( props ) {
		super( props );

		/**
		 * Our state which contains:
		 * - isDropDisabled: whether the current droppable is enabled or not, depending which draggable is the current item.
		 * - serializedData: current data
		 * - savedSerializedData: last saved data
		 * - workspace: current workspace
		 */
		this.state = {
			isDropDisabled: true,
			serializedData: '',
			savedSerializedData: '',
			workspace: this.props.workspace.present,
		};

		this.maybeShowAlert = this.maybeShowAlert.bind( this );

		let saveHappened = false;
		let showingNotice = false;

		// Check if the user has hit the publish button in Gutenberg
		window.wp.data.subscribe( () => {
			if ( false === saveHappened ) {
				saveHappened =
					window.wp.data.select( 'core/editor' ) &&
					true ===
						window.wp.data.select( 'core/editor' ).isSavingPost();
			}
			if (
				saveHappened &&
				false ===
					window.wp.data.select( 'core/editor' ).isSavingPost() &&
				false === showingNotice
			) {
				saveHappened = false;
				showingNotice = true;
				// Save happened, update our state
				this.setState(
					{ savedSerializedData: this.state.serializedData },
					() => {
						showingNotice = false;
					}
				);
			}
		} );
	}

	/**
	 * Check when we need to update the workspace to allow droppables.
	 *
	 * @param {Object} props
	 * @param {Object} state
	 */
	static getDerivedStateFromProps( props, state ) {
		const workspace = props.workspace.present;

		if (
			workspace.currentDraggableEntity !== state.currentDraggableEntity &&
			workspace.currentDraggableEntity
		) {
			let isDropDisabled = true;

			// Only Draggable lessons and section headings can be dropped on that Droppable.
			if (
				'lesson' === workspace.currentDraggableEntity.type ||
				'sfwd-lessons' === workspace.currentDraggableEntity.type ||
				'section-heading' === workspace.currentDraggableEntity.type
			) {
				isDropDisabled = false;
			}

			return {
				isDropDisabled,
				workspace,
			};
		}

		return null;
	}

	/**
	 * Shows alert if the user is trying to leave without saving
	 *
	 * @param {maybeShowAlert} e
	 */
	maybeShowAlert( e ) {
		// Classic editor
		if ( window.postL10n ) {
			if ( '' !== this.state.serializedData ) {
				return window.postL10n.saveAlert;
			}
		} else if (
			window.adminpage &&
			'sfwd-courses_page_courses-builder' === window.adminpage
		) {
			// Standalone: if they're not clicking save, show alert
			if (
				'' !== this.state.serializedData &&
				'submit' !== e.target.activeElement.id
			) {
				e.returnValue = LearnDashData.i18n.unsaved_chages;
				return e.returnValue;
			}
		} else {
			// Gutenberg
			// Our saved data is different from the current data, we need saving to avoid losing changes
			if (
				this.state.savedSerializedData !== this.state.serializedData
			) {
				e.returnValue = LearnDashData.i18n.unsaved_chages;
				return e.returnValue;
			}
		}
	}

	/**
	 * Attach events once component is mounted
	 */
	componentDidMount() {
		// Classic editor
		if ( window.postL10n ) {
			window
				.jQuery( window )
				.on( 'beforeunload.edit-post', this.maybeShowAlert );
		} else {
			// Standalone or Gutenberg
			window.addEventListener( 'beforeunload', this.maybeShowAlert );
		}
	}

	/**
	 * Detach events when component is unmounted
	 */
	componentWillUnmount() {
		// Classic editor
		if ( window.postL10n ) {
			window
				.jQuery( window )
				.off( 'beforeunload.edit-post', this.maybeShowAlert );
		} else {
			// Standalone or Gutenberg
			window.removeEventListener( 'beforeunload', this.maybeShowAlert );
		}
	}

	/**
	 * When component is updated
	 *
	 * @param {Object} prevProps The previous props
	 */
	componentDidUpdate( prevProps ) {
		// For easier reading
		const workspace = this.props.workspace.present;
		const prevWorkspace = prevProps.workspace.present;

		// Serialized the workspace as soon as it's updated.
		if ( workspace !== prevWorkspace ) {
			// Build serialized data for lessons
			let swfdData = workspace.lessons.reduce(
				( accumulator, lesson ) => {
					const lessonKey = `${ lesson.type }:${ lesson.ID }`;
					accumulator[ lessonKey ] = {};

					if ( lesson.tree && lesson.tree.length ) {
						lesson.tree.map( ( branch ) => {
							if ( branch ) {
								const branchKey = `${ branch.type }:${ branch.ID }`;
								accumulator[ lessonKey ][ branchKey ] = {};

								if ( branch.tree && branch.tree.length ) {
									branch.tree.map( ( leaf ) => {
										if ( leaf ) {
											const leafKey = `${ leaf.type }:${ leaf.ID }`;
											accumulator[ lessonKey ][
												branchKey
											][ leafKey ] = {};
										}
									} );
								}
							}
						} );
					}

					return accumulator;
				},
				{}
			);

			// Add final quizzes to the mix
			swfdData = workspace.quizzes.reduce( ( accumulator, quiz ) => {
				accumulator[ `${ quiz.type }:${ quiz.ID }` ] = {};

				return accumulator;
			}, swfdData );

			this.setState( {
				serializedData: JSON.stringify( swfdData ),
			} );
		}
	}

	/**
	 * Render function
	 *
	 * @event maybeShowAlert
	 */
	render() {
		const { workspace, totalLessons } = this.props;
		const { present } = workspace;
		return (
			<div className="ld__builder--content">
				{ /* Our main droppable where lessons can be dropped and reordered */ }
				<Droppable
					droppableId={ JSON.stringify( {
						ID: 'course-workspace-droppable',
						type: 'course',
					} ) }
					isDropDisabled={ this.state.isDropDisabled }
				>
					{ ( provided, snapshot ) => (
						<div
							ref={ provided.innerRef }
							className={ classNames( {
								'-is-dragging-over': snapshot.isDraggingOver,
							} ) }
						>
							{ /* Loop through all the lessons in our store */ }
							{ present.lessons.map( ( lesson, index ) => (
								<LessonWorkspace
									key={ lesson.ID }
									lesson={ lesson }
									index={ index }
									provided={ provided }
								/>
							) ) }

							{ /* Placeholder for dropping new lessons */ }
							<ul
								className={ classNames( {
									'ld__builder--placeholder': true,
									'-lesson': true,
									'-is-empty': 0 === totalLessons,
								} ) }
								data-label={ `${ LearnDashData.i18n.drop_lessons }` }
							>
								{ provided.placeholder }

								{ 0 === totalLessons && (
									<li>
										<EmptyEntity
											type="course"
											content="lesson"
										/>
									</li>
								) }
							</ul>
						</div>
					) }
				</Droppable>

				<div className="ld__builder--new-entities">
					<NewEntity type="lesson" />
					<NewEntity type="section-heading" />
				</div>

				{ /* Maintain backwards compatibility */ }
				<input
					type="hidden"
					id="learndash_builder_data"
					name={ `learndash_builder[sfwd-courses][${ LearnDashData.post_data.builder_post_id }]` }
					value={ this.state.serializedData }
				/>
			</div>
		);
	}
}

/**
 * Valid props
 */
CourseWorkspace.propTypes = {
	workspace: PropTypes.shape( {
		present: PropTypes.object,
	} ),
	data: PropTypes.shape( {
		labels: PropTypes.object,
	} ),
	totalLessons: PropTypes.number,
	hooks: PropTypes.object,
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = ( state ) => {
	return {
		workspace: state.workspace,
		data: state.data,
	};
};

/**
 *
 */
const mapDispatchToProps = () => ( {} );

export default connect(
	mapStateToProps,
	mapDispatchToProps
)( CourseWorkspace );
